import { sentryConfig } from './config';
import { dispatch } from './lazyClient';
import type { SentryBreadcrumb, SentryContexts } from './types';

type ContextProvider = () => Record<string, unknown>;

const contextProviders: Record<string, ContextProvider> = {};

/**
 * Records the breadcrumb built by `build` on the bundle's shared scope, also before any `init`
 * (buffered until the client loads). `build` runs inside the guard, so it can't throw into the
 * caller, and is skipped when Sentry is disabled for the build.
 */
export function addBreadcrumb(build: () => SentryBreadcrumb) {
  try {
    if (!sentryConfig.enabled) return;
    // stamped now: the scope would otherwise stamp queued crumbs at client load
    const stamped = { ...build(), timestamp: Date.now() / 1000 };
    dispatch(({ scope }) => {
      scope.addBreadcrumb(stamped, sentryConfig.maxBreadcrumbs);
    });
  } catch (e) {
    // Sentry must never break the SDK
  }
}

/**
 * Registers `provider` as `contexts.<name>` on every SDK's events, read synchronously at
 * capture time. A throwing provider only drops its own context. Re-registering replaces it.
 */
export function setContext(name: string, provider: ContextProvider) {
  try {
    if (!sentryConfig.enabled) return;
    contextProviders[name] = provider;
  } catch (e) {
    // Sentry must never break the SDK
  }
}

/** Every registered context; each provider guarded on its own. */
export function readContexts(): SentryContexts {
  const contexts: SentryContexts = {};
  Object.keys(contextProviders).forEach((name) => {
    try {
      contexts[name] = contextProviders[name]();
    } catch (e) {
      // drop only this context
    }
  });
  return contexts;
}
